<template>
  <div class="tab-page-root bg-surface text-on-surface">
    <div ref="scrollContainer" class="tab-page-scroll pb-24">
    <div class="wanted-sticky-head shadow-sm">
      <header class="w-full flex items-center justify-between px-container-margin py-base gap-gutter">
        <div class="flex items-center gap-base">
          <h1 class="font-headline-sm text-headline-sm font-bold text-primary">求购列表</h1>
        </div>
      </header>

      <nav class="py-stack-md border-b border-surface-container bg-surface">
        <div class="flex items-center gap-3 px-container-margin overflow-x-auto no-scrollbar">
          <button
            v-for="tab in tabs"
            :key="tab.key"
            class="flex-shrink-0 px-4 py-2 rounded-full font-label-md text-label-md transition-colors"
            :class="activeTab === tab.key ? 'bg-primary-container text-on-primary-container shadow-sm' : 'bg-surface-container-high text-on-surface-variant hover:bg-surface-container-highest'"
            type="button"
            @click="selectTab(tab)"
          >{{ tab.label }}</button>
        </div>
      </nav>
    </div>

    <main id="wanted-posts" class="px-container-margin mt-stack-lg space-y-gutter">
      <div v-if="loading && wantedPosts.length === 0" class="bg-surface-container-lowest border border-surface-container rounded-xl p-stack-lg text-center text-on-surface-variant">
        <span class="material-symbols-outlined text-[32px] animate-spin">progress_activity</span>
        <p class="font-body-md text-body-md mt-base">正在加载求购...</p>
      </div>

      <div v-if="errorMessage" class="bg-error-container/20 border border-error-container rounded-xl p-stack-md text-error font-body-md text-body-md" role="alert">
        <div class="flex items-center justify-between gap-3">
          <span>{{ errorMessage }}</span>
          <button class="px-3 py-1.5 rounded-full bg-error text-on-error font-label-md text-label-md active:scale-95" type="button" @click="loadWantedPosts({ reset: true, force: true })">重试</button>
        </div>
      </div>

      <article
        v-for="item in wantedPosts"
        :key="item.id"
        class="bg-surface-container-lowest rounded-xl p-stack-md wanted-card-shadow border border-surface-variant/30 flex flex-col gap-3 transition-transform active:scale-[0.98]"
      >
        <div class="flex items-center justify-between gap-3">
          <div class="flex items-center gap-3 min-w-0">
            <img alt="用户头像" class="w-10 h-10 rounded-full bg-surface-container-high object-cover" :src="item.avatar || defaultAvatar" />
            <div class="min-w-0">
              <p class="font-label-md text-label-md font-bold text-on-surface truncate">{{ item.username || '求购用户' }}</p>
              <p class="text-[10px] text-on-surface-variant/70 uppercase truncate">
                {{ item.time || '刚刚发布' }}<span v-if="item.area"> • {{ item.area }}</span>
              </p>
            </div>
          </div>
          <span v-if="item.category" class="px-3 py-1 bg-secondary-container/30 text-secondary text-[11px] font-bold rounded-full border border-secondary/10 shrink-0">{{ item.category }}</span>
        </div>

        <div class="space-y-1">
          <h2 class="font-headline-sm text-headline-sm text-on-surface leading-tight">{{ item.title }}</h2>
          <p class="font-body-md text-body-md text-on-surface-variant line-clamp-2">{{ item.introduction }}</p>
        </div>

        <div class="flex items-center justify-between gap-4 mt-1">
          <div class="flex flex-col min-w-0">
            <span class="text-[10px] text-on-surface-variant font-bold uppercase tracking-wider">预算</span>
            <span class="font-price-display text-price-display text-primary truncate">{{ item.budget }}</span>
          </div>
          <a
            v-if="item.whatsappUrl"
            class="flex items-center gap-2 px-4 py-2 bg-secondary text-on-secondary rounded-lg font-label-md text-label-md active:scale-95 transition-all shrink-0 no-underline"
            :href="item.whatsappUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span class="material-symbols-outlined text-[18px]">chat</span>
            我有这件
          </a>
          <button
            v-else
            class="flex items-center gap-2 px-4 py-2 bg-secondary text-on-secondary rounded-lg font-label-md text-label-md shrink-0 opacity-50 pointer-events-none"
            type="button"
            disabled
          >
            <span class="material-symbols-outlined text-[18px]">chat</span>
            我有这件
          </button>
        </div>
      </article>

      <div v-if="!loading && wantedPosts.length === 0 && !errorMessage" class="bg-surface-container-lowest border border-surface-container rounded-xl p-stack-lg text-center">
        <span class="material-symbols-outlined text-[40px] text-on-surface-variant">favorite_border</span>
        <p class="font-headline-sm text-headline-sm text-on-surface mt-base">暂无求购</p>
        <p class="font-body-md text-body-md text-on-surface-variant mt-1">换个分类看看，或发布一条新的求购。</p>
      </div>

      <div v-if="wantedPosts.length > 0" class="py-4 text-center">
        <button
          v-if="hasMore"
          class="w-full h-11 rounded-full bg-surface-container text-on-surface font-label-md text-label-md border border-outline-variant/30 active:scale-95 transition-all disabled:opacity-60"
          type="button"
          :disabled="loading"
          @click="loadMore"
        >{{ loading ? '加载中...' : '加载更多' }}</button>
        <p v-else class="font-label-md text-label-md text-outline">没有更多求购了</p>
      </div>
    </main>

    <button
      aria-label="发布求购"
      class="wanted-fab w-14 h-14 bg-secondary text-on-secondary rounded-full shadow-lg flex items-center justify-center active:scale-95 transition-transform"
      type="button"
      @click="$router.push({ name: 'wanted-publish' })"
    >
      <span class="material-symbols-outlined text-[28px]" style="font-variation-settings: 'FILL' 1;">add</span>
    </button>
    </div>
  </div>
</template>

<script>
import getApi from '@/api';
import pageActivate from '@/mixins/pageActivate';
import store from '@/store';

const api = getApi();
const defaultAvatar = require('@/assets/images/8ca7cfcc1ddc.jpg');

export default {
  name: 'wanted-list',
  mixins: [pageActivate],
  data() {
    return {
      defaultAvatar,
      activeTab: 'all',
      activeCategoryId: '',
      tabs: [
        { key: 'all', label: '全部', id: '' }
      ],
      page: 1,
      wantedPosts: [],
      loading: false,
      hasMore: false,
      errorMessage: '',
      categoriesLoading: false,
      requestId: 0
    };
  },
  methods: {
    fetchCategories() {
      this.categoriesLoading = true;
      api.categories(store.state.token || '')
        .then(list => {
          const categoryTabs = (list || [])
            .map(cat => ({
              key: String(cat.id),
              id: Number(cat.id) || '',
              label: cat.title || ''
            }))
            .filter(tab => tab.id && tab.label);
          this.tabs = [{ key: 'all', label: '全部', id: '' }].concat(categoryTabs);
        })
        .catch(() => {
          this.tabs = [{ key: 'all', label: '全部', id: '' }];
        })
        .finally(() => {
          this.categoriesLoading = false;
        });
    },

    selectTab(tab) {
      if (!tab || this.activeTab === tab.key) return;
      this.activeTab = tab.key;
      this.activeCategoryId = tab.id || '';
      this.page = 1;
      this.loadWantedPosts({ reset: true, force: true });
    },

    loadWantedPosts(options) {
      options = options || {};
      if (this.loading && !options.force) return Promise.resolve();

      const reset = options.reset !== false;
      const page = reset ? 1 : this.page;
      if (reset) this.page = 1;
      this.loading = true;
      this.errorMessage = '';
      this.requestId += 1;
      const requestId = this.requestId;

      return api.wannaList({
        id: this.activeCategoryId,
        page: this.page
      }, store.state.token || '')
        .then(result => {
          if (requestId !== this.requestId) return;
          const rows = Array.isArray(result.data) ? result.data : [];
          const mapped = rows.map(this.mapWantedPost);
          this.wantedPosts = reset ? mapped : this.wantedPosts.concat(mapped);
          this.page = Number(result.current_page || page);
          const lastPage = Number(result.last_page || 0);
          this.hasMore = lastPage ? this.page < lastPage : rows.length >= Number(result.per_page || 20);
          store.clearWantedListDirty();
        })
        .catch(error => {
          if (requestId !== this.requestId) return;
          if (reset) {
            this.wantedPosts = [];
            this.hasMore = false;
          }
          this.errorMessage = error.message || '求购列表加载失败';
        })
        .finally(() => {
          if (requestId !== this.requestId) return;
          this.loading = false;
        });
    },

    loadMore() {
      if (this.loading || !this.hasMore) return;
      this.page += 1;
      this.loadWantedPosts({ reset: false, force: true });
    },

    mapWantedPost(item) {
      item = item || {};
      const mobile = String(item.mobile || item.phone || '').replace(/\D/g, '');
      const code = String(item.code || '').replace(/\D/g, '');
      let whatsappUrl = String(item.url || '').trim();
      // 与售卖贴一致：后端未给 url 时用区号+手机号自行拼 WhatsApp 链接
      if (!whatsappUrl && mobile) {
        whatsappUrl = 'https://wa.me/' + code + mobile;
      }
      return {
        id: item.id,
        title: item.title || '未命名求购',
        introduction: item.introduction || item.introudction || '',
        budget: this.formatBudget(item.min_price, item.max_price),
        area: item.area || '',
        typeId: item.type_id || '',
        category: item.type_title || item.category || '',
        time: this.formatTime(item.createAt || item.createtime || item.create_time),
        avatar: item.avatar || '',
        userId: item.user_id,
        username: item.username || '',
        code,
        phone: mobile,
        mobile,
        whatsappUrl
      };
    },

    formatBudget(min, max) {
      const minText = this.formatPrice(min);
      const maxText = this.formatPrice(max);
      if (minText && maxText) return minText + ' - ' + maxText;
      return minText || maxText || '面议';
    },

    formatPrice(value) {
      if (value === null || value === undefined || value === '') return '';
      const num = Number(value);
      if (Number.isFinite(num)) {
        return '£' + num.toLocaleString('zh-CN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
      }
      return '£' + value;
    },

    formatTime(value) {
      if (!value) return '';
      if (typeof value === 'number') return this.formatDate(new Date(value > 10000000000 ? value : value * 1000));
      const str = String(value);
      if (/^\d+$/.test(str)) {
        const num = Number(str);
        return this.formatDate(new Date(num > 10000000000 ? num : num * 1000));
      }
      return str;
    },

    formatDate(date) {
      if (!date || Number.isNaN(date.getTime())) return '';
      const pad = n => String(n).padStart(2, '0');
      return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate()) + ' ' + pad(date.getHours()) + ':' + pad(date.getMinutes());
    },

    onActivated() {
      if (store.state.wantedListDirty || sessionStorage.getItem('wantedListDirty') === 'true') {
        this.page = 1;
        this.loadWantedPosts({ reset: true, force: true });
      }
    },

    onScroll() {
      const container = this.$refs.scrollContainer;
      const head = this.$el && this.$el.querySelector('.wanted-sticky-head');
      if (!head || !container) return;
      if (container.scrollTop > 10) {
        head.classList.add('shadow-md');
        head.classList.remove('shadow-sm');
      } else {
        head.classList.add('shadow-sm');
        head.classList.remove('shadow-md');
      }
    }
  },
  mounted() {
    this.fetchCategories();
    this.loadWantedPosts({ reset: true, force: true });
    const container = this.$refs.scrollContainer;
    if (container) {
      container.addEventListener('scroll', this.onScroll, { passive: true });
    }
  },
  activated() {
    this.onActivated();
  },
  beforeDestroy() {
    const container = this.$refs.scrollContainer;
    if (container) {
      container.removeEventListener('scroll', this.onScroll);
    }
  }
};
</script>

<style scoped>
body {
  font-family: 'Inter', sans-serif;
  -webkit-tap-highlight-color: transparent;
}
.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.wanted-card-shadow {
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.05);
}
.wanted-sticky-head {
  position: sticky;
  top: 0;
  z-index: 50;
  background: #f9f9fa;
}
.wanted-fab {
  position: fixed;
  right: 16px;
  bottom: 88px;
  z-index: 60;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.animate-spin {
  animation: spin 1s linear infinite;
}
</style>
